import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, BookOpen, ShoppingBag, Link2, ArrowRight, Volume2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { pickRandomVerse } from "../lib/votd";

const HERO_IMG = "https://images.pexels.com/photos/7987073/pexels-photo-7987073.jpeg";

export default function Home() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const go = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/bible?ref=${encodeURIComponent(q.trim())}`);
  };

  const listen = () => {
    const ref = q.trim() || "John 3:16";
    navigate(`/bible?ref=${encodeURIComponent(ref)}&autoplay=1`);
  };

  return (
    <div className="fade-in">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-md border border-stone-200 bg-white">
        <div className="grid md:grid-cols-2 gap-0 min-h-[440px]">
          <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
            <div className="text-xs uppercase tracking-[0.2em] text-primary/80 font-medium mb-4">A quiet place for Scripture</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-stone-900 leading-[1.05]">
              The Word,<br />wherever you are.
            </h1>
            <p className="mt-6 text-stone-700 text-lg leading-relaxed max-w-md">
              Read the Bible by book, chapter, or verse in seconds — on your phone, tablet, or desktop. No clutter. Just Scripture.
            </p>

            <form onSubmit={go} className="mt-8 flex gap-2 max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" strokeWidth={1.5} />
                <Input
                  data-testid="home-verse-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="e.g. John 3:16 or Psalm 23"
                  className="pl-9 h-12 bg-white border-stone-300"
                />
              </div>
              <Button type="submit" data-testid="home-verse-search-btn" size="lg" className="h-12">
                Read <ArrowRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
              </Button>
              <Button type="button" onClick={listen} data-testid="home-verse-listen-btn" size="lg" variant="outline" className="h-12">
                <Volume2 className="mr-1 h-4 w-4" strokeWidth={1.5} /> Listen
              </Button>
            </form>

            <div className="mt-4 flex flex-wrap gap-2 text-xs text-stone-500">
              <span className="mr-1">Try:</span>
              {["John 3:16", "Psalm 23", "Romans 8:28", "Philippians 4:13"].map((s) => (
                <button
                  key={s}
                  onClick={() => navigate(`/bible?ref=${encodeURIComponent(s)}`)}
                  data-testid={`home-suggest-${s.replace(/[^a-z0-9]/gi, "-")}`}
                  className="rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 hover:border-primary hover:text-primary transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="relative min-h-[300px] md:min-h-full">
            <img src={HERO_IMG} alt="Open Bible in morning light" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-stone-900/30 to-transparent" />
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section className="mt-16 grid md:grid-cols-3 gap-6">
        {[
          { to: "/bible", icon: BookOpen, title: "Read & Listen", desc: "Multiple translations — KJV, WEB, ASV and more. Listen to any chapter aloud with one tap.", cta: "Open Reader", tid: "home-card-bible" },
          { to: "/plans", icon: BookOpen, title: "Reading Plans", desc: "Follow a daily plan through a book, a theme, or the whole Bible — one day at a time.", cta: "Browse Plans", tid: "home-card-plans" },
          { to: "/articles", icon: BookOpen, title: "Articles", desc: "Reflections, devotionals, and teachings to encourage your walk with Christ.", cta: "Read Articles", tid: "home-card-articles" },
        ].map((c) => (
          <Link key={c.to} to={c.to} data-testid={c.tid} className="group rounded-md border border-stone-200 bg-white p-8 transition-all hover:-translate-y-1 hover:border-primary">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary mb-5">
              <c.icon className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-2xl text-stone-900">{c.title}</h3>
            <p className="mt-2 text-stone-600 leading-relaxed">{c.desc}</p>
            <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary">
              {c.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.5} />
            </div>
          </Link>
        ))}
      </section>

      {/* Verse of the Day */}
      <VerseOfTheDay />

      {/* Verse of contemplation */}
      <section className="mt-20 rounded-md border border-stone-200 bg-white p-10 sm:p-14 text-center">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">Meditation</div>
        <blockquote className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-relaxed text-stone-900 max-w-3xl mx-auto italic">
          "Come unto me, all ye that labour and are heavy laden, and I will give you rest."
        </blockquote>
        <div className="mt-5 text-stone-600">— Matthew 11:28 (KJV)</div>
      </section>
    </div>
  );
}

function VerseOfTheDay() {
  const [ref] = useState(() => pickRandomVerse());
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bible/passage", { params: { reference: ref, translation: "kjv" } })
      .then((r) => setText(r.data.text || (r.data.verses || []).map((v) => v.text.trim()).join(" ")))
      .catch(() => setText(""))
      .finally(() => setLoading(false));
  }, [ref]);

  return (
    <section className="mt-16 rounded-md border border-stone-200 bg-white p-8 sm:p-12" data-testid="verse-of-the-day">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary/80 font-medium mb-3">
        <Sparkles className="h-4 w-4" strokeWidth={1.5} /> Verse of the day
      </div>
      {loading ? (
        <div className="text-stone-500">Loading…</div>
      ) : (
        <>
          <blockquote className="font-serif text-xl sm:text-2xl lg:text-3xl leading-relaxed text-stone-900 italic max-w-4xl" data-testid="votd-text">
            "{text.trim()}"
          </blockquote>
          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <div className="text-stone-600 text-sm">— {ref} (KJV)</div>
            <Link to={`/bible?ref=${encodeURIComponent(ref)}`} data-testid="votd-read-link">
              <Button variant="outline" size="sm">Read in context <ArrowRight className="ml-1 h-4 w-4" strokeWidth={1.5} /></Button>
            </Link>
          </div>
        </>
      )}
    </section>
  );
}

